import { store, getElement, getContext, useEffect, withScope } from '@wordpress/interactivity';
import Hls from 'hls.js';

const { state, callbacks } = store('lana/scroller', {
  actions: {
    playPause() {
      const context = getContext();
      const player = callbacks.getPlayer();
      callbacks.showPlayerControls();
      if (player.paused) {
        player.play();
        context.isPlaying = true;
      } else {
        player.pause();
        context.isPlaying = false;
      }
    },
    skipBack() {
      const player = callbacks.getPlayer();
      callbacks.showPlayerControls();
      player.currentTime = player.currentTime - 10;
    },
    skipForward() {
      const player = callbacks.getPlayer();
      callbacks.showPlayerControls();
      player.currentTime = player.currentTime + 10;
    },
    muteUnmute() {
      const player = callbacks.getPlayer();
      player.muted = !player.muted;
      state.isMuted = !state.isMuted;
    },
    share() {
      const context = getContext();
      if (!context) return;
      context.isSharing = true;
      setTimeout(() => {
        context.isSharing = false;
      }, 2500);
      const url = window.location.href;
      const decodedUrl = decodeURI(url);
      navigator.clipboard.writeText(decodedUrl);
    },
  },
  callbacks: {
    showPlayerControls() {
      const context = getContext();
      context.isPlayerControlsShowing = true;

      if (state.playerControlsTimeout) {
        clearTimeout(state.playerControlsTimeout);
      }

      state.playerControlsTimeout = setTimeout(
        withScope(() => {
          const scopeContext = getContext();
          scopeContext.isPlayerControlsShowing = false;
          state.playerControlsTimeout = null;
        }),
        3000,
      );
    },
    initPlayerObserver() {
      /* eslint-disable react-hooks/rules-of-hooks */
      useEffect(() => {
        const context = getContext();
        if (context.observer) return;
        const { ref } = getElement();

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                state.selectedPost = { id: context.id, slug: context.slug, index: context.index };
              }
            });
          },
          {
            rootMargin: '0px',
            threshold: 0.95,
          },
        );
        context.observer = observer;
        observer.observe(ref);
      });
    },
    setupHlsPlayer() {
      if (!state?.selectedPost) {
        return;
      }

      const context = getContext();
      if (!context) return;

      const { index, src, programId } = context;
      if (!programId || !src) return;

      const { ref } = getElement();
      if (!ref) return;

      useEffect(() => {
        if (index < state.selectedPost.index - 1 || index > state.selectedPost.index + 1) {
          return;
        }

        window.lanaStreamPlayers = window.lanaStreamPlayers || {};

        if (window.lanaStreamPlayers[programId]) return;

        // Non IOS (Hls.js library)
        if (Hls.isSupported()) {
          const hls = new Hls();

          hls.loadSource(src);
          hls.attachMedia(ref);

          hls.on(Hls.Events.ERROR, function (event, data) {
            console.log(event, data);
            switch (data.type) {
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log('MEDIA ERROR', event, data);
                hls.recoverMediaError();
                break;
              default:
                // cannot recover
                console.log('DEFAULT ERROR', event, data);
                hls.destroy();
                break;
            }
          });

          // Native HLS (IOS, Safari)
        } else if (ref.canPlayType('application/vnd.apple.mpegurl')) {
          ref.src = src;
          ref.load();
        }

        window.lanaStreamPlayers[programId] = ref;

        if (context.observer) {
          context.observer.observe(ref);
        }

        return () => {
          context.observer.unobserve(ref);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [state.selectedPost.index]);
    },
    handlePlayPause() {
      const context = getContext();
      if (state?.selectedPost?.id === context?.id) {
        const player = callbacks.getPlayer();
        if (!player) return;
        // Muted state persists over videos.
        if (state.isMuted) {
          player.muted = true;
        } else {
          player.muted = false;
        }
        const playbackPromise = player.play();

        if (playbackPromise) {
          playbackPromise
            .then(() => {
              // Playback + audio
              player.play();
              context.isPlaying = true;
            })
            .catch(() => {
              // Mute and pause
              player.muted = true;
              state.isMuted = true;
              player.play().catch(() => {
                player.pause();
                player.currentTime = 0;
                context.isPlaying = false;
              });
            });
        }
      } else {
        const player = callbacks.getPlayer();
        if (!player) return;
        // Restart video if it's
        // no longer in view.
        player.pause();
        player.currentTime = 0;
        context.isPlaying = false;
      }
    },
    getPlayer(id) {
      const programId = id ?? getContext()?.programId;

      if (!programId) return null;

      if (!window.lanaStreamPlayers || !window.lanaStreamPlayers[programId]) {
        return null;
      }

      return window.lanaStreamPlayers[programId];
    },
  },
});
